import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Play, Pause, Square, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

const AudioRecorder = ({
  onAudioChange,
  maxDuration = 60, // 60 seconds default
  className = ''
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [uploadedAudio, setUploadedAudio] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioPlayerRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const fileInputRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Try to use WebM format as it's supported by the backend and widely supported
      let mimeType = '';
      let fileExtension = '.webm';

      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
        fileExtension = '.webm';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
        fileExtension = '.webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
        fileExtension = '.mp4'; // Note: .mp4 is not in backend allowed extensions, but keeping as fallback
      } else {
        mimeType = 'audio/webm'; // Force WebM as default since it's supported by backend
        fileExtension = '.webm';
      }

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const finalMimeType = mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: finalMimeType });

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        const filename = `recorded_audio_${timestamp}${fileExtension}`;

        // Create a proper File object instead of Blob
        const audioFile = new File([blob], filename, { type: finalMimeType });

        const url = URL.createObjectURL(audioFile);

        setAudioBlob(audioFile);
        setAudioUrl(url);
        onAudioChange(audioFile);

        // Stop all tracks to release microphone
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);

      // Start timer
      timerIntervalRef.current = setInterval(() => {
        setDuration(prev => {
          const newDuration = prev + 1;
          if (newDuration >= maxDuration) {
            stopRecording();
          }
          return newDuration;
        });
      }, 1000);

      toast.success('Recording started');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);

      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      toast.success('Recording stopped');
    }
  };

  const playAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseAudio = () => {
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
      setIsPlaying(false);
    }
  };

  const resetAudio = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }

    setAudioBlob(null);
    setAudioUrl(null);
    setUploadedAudio(null);
    setDuration(0);
    setCurrentTime(0);
    setIsPlaying(false);
    onAudioChange(null);

    if (audioPlayerRef.current) {
      audioPlayerRef.current.currentTime = 0;
    }

    toast.success('Audio removed');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file extension to match backend validation exactly
    const allowedExtensions = ['.mp3', '.wav', '.ogg', '.webm'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!allowedExtensions.includes(fileExtension)) {
      toast.error('Unsupported audio format. Please use files with .mp3, .wav, .ogg, or .webm extensions');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      toast.error('Audio file size must be less than 10MB');
      return;
    }

    // Check duration
    const audio = new Audio();
    audio.preload = 'metadata';

    audio.onloadedmetadata = () => {
      if (audio.duration > maxDuration) {
        toast.error(`Audio must be ${maxDuration} seconds or shorter`);
        URL.revokeObjectURL(audio.src);
        return;
      }

      const url = URL.createObjectURL(file);
      setUploadedAudio({ file, url });
      setAudioUrl(url);
      setAudioBlob(file);
      onAudioChange(file);
      toast.success('Audio file uploaded');
    };

    audio.onerror = () => {
      toast.error('Unable to load audio file');
      URL.revokeObjectURL(audio.src);
    };

    audio.src = URL.createObjectURL(file);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleTimeUpdate = () => {
    if (audioPlayerRef.current) {
      setCurrentTime(Math.floor(audioPlayerRef.current.currentTime));
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-3">
        Audio Recording
        <span className="text-gray-500 ml-1">(Record or upload audio, max {maxDuration}s)</span>
      </label>

      <div className="border border-gray-300 rounded-lg p-4 bg-white">
        {!audioUrl ? (
          // Recording/Upload Interface
          <div className="text-center space-y-4">
            <div className="space-y-3">
              {/* Record Button */}
              <div>
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isRecording && duration >= maxDuration}
                  className={`
                    w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200
                    ${isRecording
                      ? 'bg-red-500 hover:bg-red-600 animate-pulse'
                      : 'bg-green-500 hover:bg-green-600'
                    }
                    text-white shadow-lg hover:shadow-xl
                  `}
                >
                  {isRecording ? <Square className="h-8 w-8" /> : <Mic className="h-8 w-8" />}
                </button>

                {isRecording && (
                  <div className="mt-2 text-sm text-gray-600">
                    Recording: {formatTime(duration)} / {formatTime(maxDuration)}
                  </div>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-3 text-sm text-gray-500">OR</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>

              {/* Upload Button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isRecording}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Audio File
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mp3,audio/mpeg,audio/wav,audio/wave,audio/ogg,audio/webm,.mp3,.wav,.ogg,.webm"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>
        ) : (
          // Audio Player Interface
          <div className="space-y-4">
            <audio
              ref={audioPlayerRef}
              src={audioUrl}
              onTimeUpdate={handleTimeUpdate}
              onEnded={handleAudioEnded}
              preload="metadata"
              onLoadedMetadata={() => {
                if (audioPlayerRef.current) {
                  setDuration(Math.floor(audioPlayerRef.current.duration));
                }
              }}
            />

            {/* Controls */}
            <div className="flex items-center justify-center space-x-4">
              <button
                type="button"
                onClick={isPlaying ? pauseAudio : playAudio}
                className="w-12 h-12 rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors flex items-center justify-center"
              >
                {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </button>

              <div className="flex-1 text-center">
                <div className="text-sm text-gray-600">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-200"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={resetAudio}
                className="w-12 h-12 rounded-full bg-red-500 text-white hover:bg-red-600 transition-colors flex items-center justify-center"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            </div>

            {/* Audio Info */}
            <div className="text-center text-sm text-gray-600">
              {uploadedAudio ? (
                <span>Uploaded: {uploadedAudio.file.name}</span>
              ) : (
                <span>Recorded Audio ({formatTime(duration)})</span>
              )}
            </div>
          </div>
        )}

        {/* Guidelines */}
        <div className="mt-4 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
          <p className="font-medium mb-1">Audio Guidelines:</p>
          <ul className="space-y-1">
            <li>• Maximum recording length: {maxDuration} seconds</li>
            <li>• Supported upload formats: MP3, WAV, OGG, WebM</li>
            <li>• Maximum file size: 10MB</li>
            <li>• Record in a quiet environment for best quality</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AudioRecorder;